import React, { useState } from "react";
import ReactDOM from "react-dom";

import { BackgroundRun } from "./backgroundRuns";
import { IngestJob, IngestJobStatus } from "./ingestJobs";
import { getAlertStackContainer } from "./shared";

const STATUS_META: Record<
  IngestJobStatus,
  { label: string; dot: string; text: string }
> = {
  starting: {
    label: "Starting",
    dot: "bg-indigo-500",
    text: "text-indigo-700",
  },
  running: {
    label: "Researching",
    dot: "bg-indigo-500",
    text: "text-indigo-700",
  },
  completed: { label: "Ready", dot: "bg-green-500", text: "text-green-700" },
  failed: { label: "Failed", dot: "bg-red-500", text: "text-red-700" },
  cancelled: { label: "Cancelled", dot: "bg-gray-400", text: "text-gray-500" },
};

const timeAgo = (ts: number): string => {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const stepSummary = (job: IngestJob): string => {
  const nodes = job.progress?.nodes || [];
  if (nodes.length === 0) return "starting…";
  const done = nodes.filter((n) => n.status === "completed").length;
  const running = nodes.find((n) => n.status === "running");
  const step = `step ${Math.min(done + 1, nodes.length)} of ${nodes.length}`;
  return running?.progress ? `${step} · ${running.progress}` : step;
};

interface IngestJobsTrayProps {
  jobs: IngestJob[];
  activeCount: number;
  onOpen: (jobId: string) => void;
  onView: (jobId: string) => void;
  onCancel: (jobId: string) => void;
  onClear: (jobId: string) => void;
  onClearFinished: () => void;
  /**
   * Other in-flight background runs (grocery replacement, sourcing finder, ...)
   * that live in a component's local state rather than the ingest job manager.
   * They render alongside ingest runs so everything sits in one place.
   */
  transientRuns?: BackgroundRun[];
}

/**
 * Floating dock (bottom-right) — the single home for ALL background runs in the
 * Catalog Manager: in-flight transient runs (replacement, sourcing finder) with
 * view/cancel, plus ingest research runs (live step progress, then a prominent
 * "View results" button until cleared). Anything long-running shows up here so
 * there's one consistent "waiting" area instead of scattered pills.
 */
const IngestJobsTray: React.FC<IngestJobsTrayProps> = ({
  jobs,
  activeCount,
  onOpen,
  onView,
  onCancel,
  onClear,
  onClearFinished,
  transientRuns = [],
}) => {
  const [collapsed, setCollapsed] = useState(false);
  if (jobs.length === 0 && transientRuns.length === 0) return null;

  const finishedCount = jobs.filter(
    (j) => j.status !== "running" && j.status !== "starting"
  ).length;
  const readyCount = jobs.filter((j) => j.status === "completed").length;
  const runningCount = activeCount + transientRuns.length;

  const container = getAlertStackContainer();
  if (!container) return null;

  // `order-last` pins the tray to the bottom of the shared stack (nearest the
  // corner); transient toasts stack above it. Keeps everything from overlapping.
  return ReactDOM.createPortal(
    <div className="pointer-events-auto order-last w-[22rem] max-w-[calc(100vw-2rem)]">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
        {/* Header */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex w-full items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left"
        >
          <span className="flex items-center gap-2">
            {runningCount > 0 ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-600" />
            ) : (
              <span className="text-sm">📥</span>
            )}
            <span className="text-sm font-semibold text-gray-800">
              Background runs
            </span>
            {runningCount > 0 && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                {runningCount} running
              </span>
            )}
            {readyCount > 0 && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                {readyCount} ready
              </span>
            )}
          </span>
          <span className="text-xs text-gray-500">{collapsed ? "▸" : "▾"}</span>
        </button>

        {!collapsed && (
          <>
            <ul className="max-h-[24rem] divide-y divide-gray-100 overflow-auto">
              {/* Transient in-flight runs (replacement / sourcing finder). */}
              {transientRuns.map((run) => (
                <li key={run.id} className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-600" />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {run.title}
                      </span>
                      <div className="text-xs text-indigo-700">
                        {run.detail || "Running…"}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {run.onView && (
                          <button
                            type="button"
                            onClick={run.onView}
                            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                          >
                            View
                          </button>
                        )}
                        {run.onCancel && (
                          <button
                            type="button"
                            onClick={run.onCancel}
                            className="text-xs font-medium text-gray-500 underline decoration-dotted hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}

              {jobs.map((job) => {
                const meta = STATUS_META[job.status];
                const inFlight =
                  job.status === "running" || job.status === "starting";
                return (
                  <li key={job.id} className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-gray-900">
                            {job.label}
                          </span>
                          <span className="flex-shrink-0 text-[11px] text-gray-400">
                            {timeAgo(job.createdAt)}
                          </span>
                        </div>
                        <div className={`text-xs ${meta.text}`}>
                          {meta.label}
                          {inFlight ? ` · ${stepSummary(job)}` : ""}
                          {job.status === "completed" &&
                            typeof job.resultCount === "number" &&
                            ` · ${job.resultCount} product${
                              job.resultCount === 1 ? "" : "s"
                            }`}
                          {job.status === "completed" &&
                            typeof job.writtenCount === "number" &&
                            job.writtenCount > 0 &&
                            ` · ✓ ${job.writtenCount} written`}
                        </div>
                        {job.status === "completed" &&
                          job.excluded &&
                          job.excluded.length > 0 && (
                            <div className="mt-0.5 text-xs text-amber-700">
                              {job.excluded.length === 1
                                ? `Skipped “${job.excluded[0].raw_input}” — too general`
                                : `${job.excluded.length} skipped — too general`}
                              . Add a brand to research it.
                            </div>
                          )}
                        {job.status === "completed" &&
                          job.resultCount === 0 &&
                          !job.excluded?.length && (
                            <div className="mt-0.5 text-xs text-amber-700">
                              No products found. Try the brand and full name, a
                              UPC, or a link.
                            </div>
                          )}
                        {job.status === "failed" && job.error && (
                          <div className="mt-0.5 truncate text-xs text-red-500">
                            {job.error}
                          </div>
                        )}
                        {job.status === "completed" &&
                          !job.resultItems &&
                          job.resultCount === undefined && (
                            <div className="mt-0.5 text-xs text-gray-400">
                              Results are no longer cached — re-run to review.
                            </div>
                          )}

                        {/* Actions */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {inFlight && (
                            <>
                              <button
                                type="button"
                                onClick={() => onOpen(job.id)}
                                className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                onClick={() => onCancel(job.id)}
                                className="text-xs font-medium text-gray-500 underline decoration-dotted hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {job.status === "completed" && job.resultItems && (
                            <button
                              type="button"
                              onClick={() => onView(job.id)}
                              className="rounded-md bg-green-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-green-700"
                            >
                              {job.resultItems.length > 0
                                ? "View results"
                                : "See why"}
                            </button>
                          )}
                          {!inFlight && (
                            <button
                              type="button"
                              onClick={() => onClear(job.id)}
                              className="text-xs font-medium text-gray-500 underline decoration-dotted hover:text-gray-700"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {finishedCount > 0 && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-right">
                <button
                  type="button"
                  onClick={onClearFinished}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800"
                >
                  Clear finished ({finishedCount})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    container
  );
};

export default IngestJobsTray;
